import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, serverTimestamp, orderBy, limit } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { SHIFT_TYPES } from '../../../data/nurseAppModels';

const SwapHistoryPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const [swapHistory, setSwapHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedSwap, setSelectedSwap] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseType, setResponseType] = useState(null); // 'approve' or 'reject'
  const [rejectionReason, setRejectionReason] = useState('');
  const [filter, setFilter] = useState({
    status: 'ALL', // ALL, PENDING, MEMBER_APPROVED, APPROVED, REJECTED, CANCELLED
    type: 'ALL', // ALL, SENT, RECEIVED
    search: ''
  });

  useEffect(() => {
    if (!currentTeam || !currentUser) return;

    const fetchSwapHistory = async () => {
      try {
        setLoading(true);
        const swapsRef = collection(db, 'shiftSwaps');
        
        // Query only by teamId to avoid index requirement
        // We'll filter by type and status in memory, and sort in memory too
        let q = query(
          swapsRef,
          where('teamId', '==', currentTeam.id),
          limit(200) // Get more to filter in memory
        );

        const snapshot = await getDocs(q);
        console.log('[SwapHistoryPage] Total swaps fetched:', snapshot.docs.length);
        
        const swaps = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const data = docSnap.data();
            
            // Fetch user data for requestedBy and requestedTo
            const [requestedByUser, requestedToUser] = await Promise.all([
              getDoc(doc(db, 'users', data.requestedBy)),
              getDoc(doc(db, 'users', data.requestedTo))
            ]);

            // Fetch requested shift if exists
            let requestedShift = null;
            if (data.requestedShiftId) {
              const shiftDoc = await getDoc(doc(db, 'shifts', data.requestedShiftId));
              if (shiftDoc.exists()) {
                requestedShift = { id: shiftDoc.id, ...shiftDoc.data() };
              }
            }

            return {
              id: docSnap.id,
              ...data,
              requestedByUser: requestedByUser.exists() ? requestedByUser.data() : null,
              requestedToUser: requestedToUser.exists() ? requestedToUser.data() : null,
              requestedShift: requestedShift
            };
          })
        );

        console.log('[SwapHistoryPage] All swaps processed:', swaps.length);
        console.log('[SwapHistoryPage] Current filter:', filter);
        console.log('[SwapHistoryPage] Current user:', currentUser.uid);

        // Filter by type (SENT or RECEIVED) in memory
        let filteredSwaps = swaps;
        if (filter.type === 'SENT') {
          filteredSwaps = swaps.filter(s => {
            const isSent = s.requestedBy === currentUser.uid;
            console.log('[SwapHistoryPage] Swap', s.id, 'is SENT?', isSent, 'requestedBy:', s.requestedBy);
            return isSent;
          });
          console.log('[SwapHistoryPage] After SENT filter:', filteredSwaps.length);
        } else if (filter.type === 'RECEIVED') {
          filteredSwaps = swaps.filter(s => {
            const isReceived = s.requestedTo === currentUser.uid;
            console.log('[SwapHistoryPage] Swap', s.id, 'is RECEIVED?', isReceived, 'requestedTo:', s.requestedTo);
            return isReceived;
          });
          console.log('[SwapHistoryPage] After RECEIVED filter:', filteredSwaps.length);
        } else {
          // ALL - show swaps where user is either requestedBy or requestedTo
          filteredSwaps = swaps.filter(s => 
            s.requestedBy === currentUser.uid || s.requestedTo === currentUser.uid
          );
          console.log('[SwapHistoryPage] After ALL filter (user involved):', filteredSwaps.length);
        }

        // Filter by status in memory
        if (filter.status !== 'ALL') {
          const beforeStatusFilter = filteredSwaps.length;
          filteredSwaps = filteredSwaps.filter(s => s.status === filter.status);
          console.log('[SwapHistoryPage] After status filter:', filter.status, 'from', beforeStatusFilter, 'to', filteredSwaps.length);
        }

        // Filter by search term
        if (filter.search) {
          const searchLower = filter.search.toLowerCase();
          filteredSwaps = filteredSwaps.filter(s => 
            s.requestedByName?.toLowerCase().includes(searchLower) ||
            s.requestedToName?.toLowerCase().includes(searchLower) ||
            s.message?.toLowerCase().includes(searchLower)
          );
        }

        // Sort by createdAt descending (in memory)
        filteredSwaps.sort((a, b) => {
          const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 
                       a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
          const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 
                       b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0;
          return bTime - aTime; // Descending
        });

        console.log('[SwapHistoryPage] Final filtered swaps:', filteredSwaps.length);
        setSwapHistory(filteredSwaps);
      } catch (err) {
        console.error('[SwapHistoryPage] Error fetching swap history:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSwapHistory();
  }, [currentTeam, currentUser, filter]);

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getShiftTypeLabel = (type) => {
    switch (type) {
      case SHIFT_TYPES.MORNING: return 'เช้า';
      case SHIFT_TYPES.AFTERNOON: return 'บ่าย';
      case SHIFT_TYPES.NIGHT: return 'ดึก';
      default: return type;
    }
  };

  const getShiftTypeIcon = (type) => {
    switch (type) {
      case SHIFT_TYPES.MORNING: return '🌅';
      case SHIFT_TYPES.AFTERNOON: return '☀️';
      case SHIFT_TYPES.NIGHT: return '🌙';
      default: return '📅';
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'PENDING': { text: 'รอตอบรับ', color: 'bg-yellow-100 text-yellow-800' },
      'MEMBER_APPROVED': { text: 'รอหัวหน้าอนุมัติ', color: 'bg-blue-100 text-blue-800' },
      'APPROVED': { text: 'อนุมัติแล้ว', color: 'bg-green-100 text-green-800' },
      'REJECTED': { text: 'ปฏิเสธ', color: 'bg-red-100 text-red-800' },
      'CANCELLED': { text: 'ยกเลิก', color: 'bg-gray-100 text-gray-800' }
    };
    const badge = badges[status] || { text: status, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  const isMyRequest = (swap) => {
    return swap.requestedBy === currentUser?.uid;
  };

  const canRespond = (swap) => {
    return swap.status === 'PENDING' && swap.requestedTo === currentUser?.uid;
  };

  // Handle approve/reject
  const handleResponse = async (swapId, type) => {
    setSelectedSwap(swapId);
    setResponseType(type);
    setRejectionReason('');
    setShowResponseModal(true);
  };

  // Submit response
  const submitResponse = async () => {
    if (!selectedSwap) return;

    if (responseType === 'reject' && !rejectionReason.trim()) {
      alert('กรุณากรอกเหตุผลในการปฏิเสธ');
      return;
    }

    setProcessing(true);
    try {
      const swapRef = doc(db, 'shiftSwaps', selectedSwap);
      const swapSnap = await getDoc(swapRef);
      
      if (!swapSnap.exists()) {
        throw new Error('ไม่พบคำขอแลกเวร');
      }

      const swapData = swapSnap.data();

      if (swapData.requestedTo !== currentUser.uid) {
        throw new Error('คุณไม่ใช่ผู้รับคำขอ');
      }

      if (responseType === 'approve') {
        // Check if swap requires leader approval (from plan)
        if (swapData.planId || swapData.requiresLeaderApproval) {
          // Update swap status to MEMBER_APPROVED (waiting for leader)
          await updateDoc(swapRef, {
            status: 'MEMBER_APPROVED',
            memberApprovedAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          alert('อนุมัติคำขอแลกเวรเรียบร้อยแล้ว! รอหัวหน้าทีมอนุมัติ');
        } else {
          // Complete swap immediately (not from plan)
          await updateDoc(swapRef, {
            status: 'APPROVED',
            memberApprovedAt: serverTimestamp(),
            approvedAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          alert('อนุมัติคำขอแลกเวรเรียบร้อยแล้ว!');
        }
      } else {
        // Update swap status to REJECTED
        await updateDoc(swapRef, {
          status: 'REJECTED',
          rejectedAt: serverTimestamp(),
          rejectionReason: rejectionReason.trim(),
          updatedAt: serverTimestamp()
        });
        alert('ปฏิเสธคำขอแลกเวรเรียบร้อยแล้ว!');
      }

      setShowResponseModal(false);
      setSelectedSwap(null);
      setResponseType(null);
      setRejectionReason('');
      
      // Refresh data
      window.location.reload();
    } catch (err) {
      console.error('Error responding to swap request:', err);
      alert('เกิดข้อผิดพลาด: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">ประวัติการแลกเวร</h1>
              <p className="text-sm text-gray-600 mt-1">
                ทั้งหมด {swapHistory.length} รายการ
              </p>
            </div>
            <button
              onClick={() => navigate('/nurse/shifts')}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
            >
              กลับ
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                สถานะ
              </label>
              <select
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ALL">ทั้งหมด</option>
                <option value="PENDING">รอตอบรับ</option>
                <option value="MEMBER_APPROVED">รอหัวหน้าอนุมัติ</option>
                <option value="APPROVED">อนุมัติแล้ว</option>
                <option value="REJECTED">ปฏิเสธ</option>
                <option value="CANCELLED">ยกเลิก</option>
              </select>
            </div>

            {/* Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ประเภท
              </label>
              <select
                value={filter.type}
                onChange={(e) => setFilter({ ...filter, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ALL">ทั้งหมด</option>
                <option value="SENT">ที่ฉันส่ง</option>
                <option value="RECEIVED">ที่ฉันรับ</option>
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ค้นหา
              </label>
              <input
                type="text"
                value={filter.search}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                placeholder="ค้นหาชื่อ, ข้อความ..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Swap History List */}
        {swapHistory.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500 text-lg">ไม่พบประวัติการแลกเวร</p>
          </div>
        ) : (
          <div className="space-y-4">
            {swapHistory.map((swap) => {
              const originalShiftDate = swap.originalShift?.startTime?.toDate ? 
                swap.originalShift.startTime.toDate() : null;
              const requestedShiftDate = swap.requestedShift?.startTime?.toDate ? 
                swap.requestedShift.startTime.toDate() : null;

              return (
                <div
                  key={swap.id}
                  className="bg-white rounded-lg shadow-sm p-6 border border-gray-200"
                >
                  <div className="flex items-start gap-4">
                    {/* Swap Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-2xl">{getShiftTypeIcon(swap.originalShift?.shiftType)}</span>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {isMyRequest(swap) ? (
                            <>ฉัน ↔ {swap.requestedToName}</>
                          ) : (
                            <>{swap.requestedByName} ↔ ฉัน</>
                          )}
                        </h3>
                        {getStatusBadge(swap.status)}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {/* Original Shift */}
                        <div className={`rounded-lg p-4 ${
                          isMyRequest(swap) ? 'bg-blue-50' : 'bg-gray-50'
                        }`}>
                          <p className="text-sm text-gray-600 mb-1">
                            {isMyRequest(swap) ? 'เวรที่ฉันต้องการแลก' : 'เวรที่ต้องการแลก'}
                          </p>
                          <p className="font-semibold text-gray-900">
                            {swap.requestedByName} - เวร{getShiftTypeLabel(swap.originalShift?.shiftType)}
                          </p>
                          {originalShiftDate && (
                            <p className="text-sm text-gray-600 mt-1">
                              {formatDateTime(swap.originalShift.startTime)}
                            </p>
                          )}
                        </div>

                        {/* Requested Shift */}
                        {swap.requestedShiftId ? (
                          <div className={`rounded-lg p-4 ${
                            isMyRequest(swap) ? 'bg-green-50' : 'bg-blue-50'
                          }`}>
                            <p className="text-sm text-gray-600 mb-1">
                              {isMyRequest(swap) ? 'เวรที่ฉันเสนอแลก' : 'เวรที่เสนอแลก'}
                            </p>
                            <p className="font-semibold text-gray-900">
                              {swap.requestedToName} - เวร{getShiftTypeLabel(swap.requestedShift?.shiftType || '')}
                            </p>
                            {requestedShiftDate && (
                              <p className="text-sm text-gray-600 mt-1">
                                {formatDateTime(swap.requestedShift.startTime)}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">แลกแบบ one-way</p>
                            <p className="font-semibold text-gray-900">
                              {swap.requestedToName} จะได้รับเวรของ {swap.requestedByName}
                            </p>
                          </div>
                        )}
                      </div>

                      {swap.message && (
                        <div className="bg-gray-50 rounded-lg p-3 mb-4">
                          <p className="text-sm text-gray-600 mb-1">ข้อความ:</p>
                          <p className="text-sm text-gray-900">{swap.message}</p>
                        </div>
                      )}

                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                        <span>ส่งคำขอเมื่อ: {formatDateTime(swap.createdAt)}</span>
                        {swap.memberApprovedAt && (
                          <span>• ตอบรับเมื่อ: {formatDateTime(swap.memberApprovedAt)}</span>
                        )}
                        {swap.approvedAt && (
                          <span>• อนุมัติเมื่อ: {formatDateTime(swap.approvedAt)}</span>
                        )}
                        {swap.rejectedAt && (
                          <span>• ปฏิเสธเมื่อ: {formatDateTime(swap.rejectedAt)}</span>
                        )}
                      </div>

                      {/* Action Buttons - Show only if user can respond */}
                      {canRespond(swap) && (
                        <div className="flex gap-2 pt-4 border-t border-gray-200">
                          <button
                            onClick={() => handleResponse(swap.id, 'approve')}
                            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                          >
                            ตอบรับ
                          </button>
                          <button
                            onClick={() => handleResponse(swap.id, 'reject')}
                            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                          >
                            ปฏิเสธ
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Response Modal */}
        {showResponseModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {responseType === 'approve' ? 'ยืนยันการตอบรับ' : 'ปฏิเสธคำขอแลกเวร'}
              </h3>

              {responseType === 'reject' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    เหตุผลในการปฏิเสธ *
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows="3"
                    placeholder="กรุณากรอกเหตุผล..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                  />
                </div>
              )}

              {responseType === 'approve' && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    {swapHistory.find(s => s.id === selectedSwap)?.planId 
                      ? 'การตอบรับนี้จะส่งไปยังหัวหน้าทีมเพื่ออนุมัติขั้นสุดท้าย'
                      : 'การแลกเวรจะเกิดขึ้นทันที'}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowResponseModal(false);
                    setSelectedSwap(null);
                    setResponseType(null);
                    setRejectionReason('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  ยกเลิก
                </button>
                <button
                  onClick={submitResponse}
                  disabled={processing || (responseType === 'reject' && !rejectionReason.trim())}
                  className={`flex-1 px-4 py-2 rounded-lg text-white transition disabled:opacity-50 disabled:cursor-not-allowed ${
                    responseType === 'approve'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {processing ? 'กำลังดำเนินการ...' : responseType === 'approve' ? 'ยืนยัน' : 'ปฏิเสธ'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SwapHistoryPage;



